/**
 * Pure private-room match rules: how a match ends and whether the dead may
 * respawn. Kept protocol-free so the realtime server and tests share one
 * source of truth. The realtime server maps its config to these inputs.
 */
const DEFAULT_MIN_PARTICIPANTS = 2;
function topScorerId(standings) {
    let best = null;
    for (const standing of standings) {
        // Strict > keeps the earliest entry on ties (stable winner).
        if (best === null || standing.score > best.score) {
            best = standing;
        }
    }
    return best?.id ?? null;
}
/**
 * Decides whether a private match has ended and who won.
 * - timed modes end when `now` reaches `endsAt`; winner is the top scorer.
 * - survival modes end once at most one participant is alive (needs at least
 *   `minParticipants` to avoid an instant win); winner is the last survivor,
 *   or the top scorer if everyone died together.
 */
export function evaluateMatchEnd(input) {
    const minParticipants = input.minParticipants ?? DEFAULT_MIN_PARTICIPANTS;
    if (input.winCondition === "timed_score" ||
        input.winCondition === "team_timed_score") {
        if (input.endsAt !== null && input.now >= input.endsAt) {
            return { ended: true, winnerId: topScorerId(input.standings) };
        }
        return { ended: false, winnerId: null };
    }
    // Survival-style (last_survivor / team_elimination / first_to_realm fallback).
    if (input.standings.length < minParticipants) {
        return { ended: false, winnerId: null };
    }
    const alive = input.standings.filter((standing) => standing.isAlive);
    if (alive.length <= 1) {
        return {
            ended: true,
            winnerId: alive[0]?.id ?? topScorerId(input.standings),
        };
    }
    return { ended: false, winnerId: null };
}
/** Survival modes never allow respawn; other modes honor the room setting. */
export function isRespawnAllowed(winCondition, respawnEnabled) {
    if (winCondition === "last_survivor" || winCondition === "team_elimination") {
        return false;
    }
    return respawnEnabled;
}
